import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './GuidePage.css';
import Navigation from './Navigation';
import { usePageTracking } from './hooks/useAnalytics';

function AccuracyGuidePage() {
  usePageTracking('accuracy_guide', {
    page_title: 'How Accurate Should a Mechanical Watch Be?'
  });

  return (
    <div className="guide-page">
      <Helmet>
        <title>How Accurate Should a Mechanical Watch Be? | ChronoLog</title>
        <meta name="description" content="Most healthy mechanical watches run within about -10 to +20 seconds per day. COSC chronometers must hold -4/+6. See normal ranges, standards, and when to service." />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="How Accurate Should a Mechanical Watch Be?" />
        <meta property="og:description" content="Typical mechanical watch accuracy ranges, certification standards compared (COSC, METAS, Superlative, JIS), what affects daily rate, and when poor accuracy means a service is due." />
        <meta property="og:url" content="https://www.chronolog.watch/accuracy" />
        <meta property="og:image" content="https://www.chronolog.watch/og-image.png" />
        <link rel="canonical" href="https://www.chronolog.watch/accuracy" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                "headline": "How Accurate Should a Mechanical Watch Be?",
                "description": "What counts as normal accuracy for a mechanical or automatic watch: typical daily rate ranges, certification standards (COSC, METAS, Superlative Chronometer, JIS) compared, the factors that change accuracy day to day, and when poor timekeeping means a service is due.",
                "author": { "@type": "Organization", "name": "ChronoLog", "url": "https://www.chronolog.watch" },
                "publisher": { "@type": "Organization", "name": "ChronoLog", "url": "https://www.chronolog.watch" },
                "url": "https://www.chronolog.watch/accuracy",
                "datePublished": "2026-10-12",
                "keywords": "mechanical watch accuracy, automatic watch accuracy, seconds per day, COSC, METAS, chronometer, watch daily rate, watch losing time"
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "Is ±20 seconds a day normal for an automatic watch?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes, for many non-certified automatic movements ±20 seconds per day is within the manufacturer's published tolerance. Workhorse movements like the Seiko 4R36 (-35/+45 s/day) and Miyota 8215 (-20/+40 s/day) explicitly allow more than that. Higher-grade movements and certified chronometers are held much tighter: COSC requires -4/+6 seconds per day. What matters most is consistency — a watch that steadily gains 15 seconds every day is behaving normally, while one that swings unpredictably may need attention."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What accuracy is COSC?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "COSC (Contrôle Officiel Suisse des Chronomètres) certifies a mechanical movement as a chronometer if its average daily rate stays between -4 and +6 seconds per day. Testing follows the ISO 3159 standard: roughly 15 days of measurement across five positions and three temperatures. Note that COSC tests the uncased movement, so the finished watch on your wrist can perform slightly differently."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Do mechanical watches get less accurate over time?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes, gradually. As lubricants in the movement age and degrade, friction increases, balance amplitude drops, and the rate typically drifts — often slower, and more position-dependent. This is normal wear, not a defect, and a routine service usually restores original performance. Most manufacturers recommend a service roughly every five to ten years, depending on the brand. A sudden accuracy change, by contrast, usually points to magnetization or a shock rather than aging."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Is quartz more accurate than mechanical?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes, by a wide margin. An ordinary quartz watch typically keeps time within about ±15-20 seconds per month, while even an excellent mechanical chronometer is specified in seconds per day. A quartz crystal oscillates at 32,768 Hz and is far less affected by position and winding state than a mechanical balance wheel. People choose mechanical watches for craft, longevity, and enjoyment — not because they outperform quartz on accuracy."
                    }
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>

      <Navigation showAndroidButton={false} />

      <article className="article">
        <h1>How Accurate Should a Mechanical Watch Be?</h1>
        <div className="article-meta">October 2026</div>

        <p className="article-lead">
          A healthy mechanical watch typically runs within about &minus;10 to +20 seconds per day, and many modern movements do noticeably better. A COSC-certified chronometer must keep &minus;4/+6 seconds per day, Omega&apos;s METAS-certified watches are held to 0/+5, and Rolex promises &plusmn;2. If your watch stays inside its manufacturer&apos;s published range and gains or loses a <em>consistent</em> amount each day, it is working as designed.
        </p>

        <h2>What counts as normal accuracy</h2>

        <p>
          &ldquo;Accuracy&rdquo; for a mechanical watch is usually expressed as a daily rate: how many seconds the watch gains (+) or loses (&minus;) in 24 hours. Unlike quartz, a mechanical movement keeps time with a balance wheel &mdash; a tiny oscillating flywheel &mdash; and its rate shifts with position, winding state, and temperature. So there is no single number; there is a range, and the range depends on what grade of movement you own.
        </p>

        <p>
          Rough tiers, based on manufacturers&apos; published tolerances:
        </p>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Movement tier</th>
                <th>Typical published tolerance</th>
                <th>Examples</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Entry-level workhorse</td>
                <td>&minus;20 to +40 s/day or wider</td>
                <td>Seiko 4R36 (&minus;35/+45), Miyota 8215 (&minus;20/+40)</td>
              </tr>
              <tr>
                <td>Standard Swiss ébauche</td>
                <td>about &plusmn;12 s/day average</td>
                <td>ETA 2824-2 / Sellita SW200 standard grade</td>
              </tr>
              <tr>
                <td>Upper grades</td>
                <td>about &plusmn;4 to &plusmn;7 s/day</td>
                <td>ETA/Sellita élaboré and top grades, Miyota 9015 (&minus;10/+30)</td>
              </tr>
              <tr>
                <td>Certified chronometer</td>
                <td>&minus;4/+6 s/day or tighter</td>
                <td>COSC, METAS, Superlative Chronometer</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>
          Why do tiers exist at all? Often the higher grades are the <em>same basic movement</em>, adjusted and tested more carefully. An ETA 2824-2 in top grade differs from the standard grade mainly in regulation (fine adjustment of the balance), the quality of the balance spring and shock protection, and how many positions it was adjusted in at the factory. That&apos;s also why a well-regulated budget movement can outperform its paper spec: the tolerance describes the worst the manufacturer will ship, not the best the design can do.
        </p>

        <p>
          Two things matter more than the raw number. First, <strong>consistency</strong>: a watch that gains 12 seconds every single day is easy to live with (and easy for a watchmaker to regulate); a watch that gains 5 one day and loses 15 the next has a problem even though both numbers look modest. Second, <strong>direction</strong>: many owners prefer a watch that runs slightly fast, because it&apos;s easier to notice and reset than one that quietly falls behind.
        </p>

        <h2>Accuracy standards compared: COSC, METAS, Superlative, JIS</h2>

        <p>
          Certification standards exist so that &ldquo;chronometer&rdquo; on a dial means something testable. Each body publishes its own tolerance and test protocol:
        </p>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Standard</th>
                <th>Certifying body</th>
                <th>Daily rate spec</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>COSC chronometer</td>
                <td>Contrôle Officiel Suisse des Chronomètres</td>
                <td>&minus;4/+6 s/day</td>
                <td>ISO 3159: ~15 days, 5 positions, 3 temperatures; tests the uncased movement</td>
              </tr>
              <tr>
                <td>Master Chronometer (METAS)</td>
                <td>Swiss Federal Institute of Metrology, used by Omega and others</td>
                <td>0/+5 s/day</td>
                <td>Tests the cased watch, including exposure to 15,000 gauss magnetic fields; requires prior COSC certification</td>
              </tr>
              <tr>
                <td>Superlative Chronometer</td>
                <td>Rolex (in-house)</td>
                <td>&plusmn;2 s/day</td>
                <td>Applied after casing, on top of COSC certification of the movement</td>
              </tr>
              <tr>
                <td>Grand Seiko Standard</td>
                <td>Seiko (in-house)</td>
                <td>&minus;3/+5 s/day</td>
                <td>17-day static test across 6 positions and 3 temperatures; the Special Standard is tighter still</td>
              </tr>
              <tr>
                <td>JIS chronometer</td>
                <td>Japanese Industrial Standards (JIS B 7021)</td>
                <td>&minus;4/+6 s/day</td>
                <td>Japan&apos;s chronometer standard, closely mirroring ISO 3159</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>
          A detail worth knowing: these are <em>average</em> daily rates measured under controlled conditions. On a wrist &mdash; with real temperature swings, varied positions overnight, and changing power reserve &mdash; a COSC watch can legitimately read slightly outside &minus;4/+6 on a given day and still be perfectly in spec. How brands&apos; published specs compare to what owners actually measure is covered in our guide to{' '}
          <Link to="/brand-accuracy">expected accuracy by brand</Link>.
        </p>

        <h2>What affects accuracy day to day</h2>

        <p>
          Four factors explain most of the day-to-day variation in a mechanical watch&apos;s rate:
        </p>

        <p>
          <strong>Position.</strong> Gravity acts on the balance wheel differently depending on orientation. Dial-up, dial-down, and the four crown positions each produce a slightly different rate &mdash; that&apos;s why certification tests measure multiple positions. You can use this deliberately: if your watch runs a touch fast, resting it crown-down or crown-up overnight often changes the overnight rate enough to compensate. Which position does what varies by movement, so you have to measure your own watch.
        </p>

        <p>
          <strong>Winding state.</strong> A mainspring delivers less torque as it winds down, which lowers balance amplitude and usually shifts the rate near the end of the power reserve. This effect is called isochronism error. An automatic worn daily stays near full wind; the same watch left on a desk for two days may time differently in its final hours of reserve.
        </p>

        <p>
          <strong>Temperature.</strong> Modern balance springs are made from temperature-compensating alloys, but heat and cold still nudge the rate by a few seconds per day at the extremes &mdash; which is why chronometer testing includes three temperatures.
        </p>

        <p>
          <strong>Magnetism.</strong> The sneaky one. A magnetized balance spring can make a watch suddenly run dramatically fast &mdash; sometimes minutes per day &mdash; because the coils stick together and shorten the spring&apos;s effective length. Phone cases, laptop speakers, and bag clasps are common culprits. The fix (demagnetization) is quick and cheap. If your watch&apos;s rate changed abruptly, start with our guide to{' '}
          <Link to="/watch-running-fast">why a watch runs fast or slow</Link>.
        </p>

        <h2>How to measure your watch&apos;s real rate</h2>

        <p>
          Published specs tell you what the movement <em>should</em> do; only measurement tells you what your watch actually does. The principle is simple: compare the watch against a reliable reference time, wait, compare again, and divide the drift by the elapsed time. Do this over several days &mdash; a single 24-hour reading mixes together position and winding effects, while a multi-day average smooths them out. Our step-by-step guide to{' '}
          <Link to="/measure-watch-accuracy">measuring watch accuracy at home</Link>{' '}
          walks through doing this with no equipment at all.
        </p>

        <p>
          Time is your friend here. Any manual check carries a reading error of a second or two, but that error gets divided by the measurement window: a 2-second reading error over 24 hours muddies the result by 2 s/day, while the same error over four days contributes only 0.5 s/day. Two or three checks spread across several days of normal wear will give you a trustworthy average daily rate. Note the conditions too &mdash; worn versus resting, and which position it rested in &mdash; because those are exactly the variables that move the number.
        </p>

        <p>
          The practical challenge is precision: reading a watch dial against a clock by eye introduces a second or so of error at each check, so short measurement windows produce noisy numbers. This is the problem an app like ChronoLog solves &mdash; it timestamps each check against atomic time using quick-tap and photo measurement modes, tracks the accuracy history of each watch in a collection, and can even measure rate and beat error acoustically through the phone&apos;s microphone (typically within &plusmn;2&ndash;5 s/day, no extra hardware).
        </p>

        <p>
          The other route is a timegrapher &mdash; a device (or app) that listens to the escapement and reports the instantaneous rate in seconds. It answers in moments what dial-watching answers in days, though it shows a snapshot in one position rather than real on-wrist performance. See{' '}
          <Link to="/what-is-a-timegrapher">what a timegrapher is and how to read one</Link>, or the technical deep-dive on{' '}
          <Link to="/timegrapher">building a timegrapher from a phone microphone</Link>.
        </p>

        <h2>When bad accuracy means a service is due</h2>

        <p>
          Accuracy is also a health report. The pattern of the error tells you which problem you likely have:
        </p>

        <p>
          <strong>Gradual slowing over months or years</strong> is the classic service signal. Aging lubricants increase friction, balance amplitude falls, and the rate drifts &mdash; often slower and more erratic across positions. Most manufacturers recommend a service roughly every five to ten years; a watch that has quietly gone from +3 to &minus;15 s/day since its last service is telling you it&apos;s time.
        </p>

        <p>
          <strong>Sudden large gains</strong> &mdash; a watch that was fine last week and now gains two minutes a day &mdash; point to magnetization, not wear. That&apos;s a five-minute demagnetizer fix, not a full service.
        </p>

        <p>
          <strong>Sudden erratic behavior after a knock</strong> (stopping, wildly inconsistent rate) suggests shock damage to the balance or escapement and deserves a watchmaker&apos;s inspection promptly.
        </p>

        <p>
          <strong>A steady rate outside spec</strong> &mdash; say, a consistent +25 s/day on a movement rated &plusmn;12 &mdash; often needs only regulation, a minor adjustment of the effective balance spring length, rather than a teardown. Bring a few days of measurements to the watchmaker; a documented daily rate across positions makes their job faster and your conversation concrete.
        </p>

        <div className="article-cta">
          <h2>Know your watch&apos;s real numbers</h2>
          <p>
            ChronoLog measures your watches against atomic time and tracks each one&apos;s accuracy history &mdash; $5.99 one-time on the{' '}
            <a href="https://apps.apple.com/us/app/chronolog-watch-accuracy/id6452083510">App Store</a>.
          </p>
        </div>

        <div className="related-guides">
          <h2>Related guides</h2>
          <ul>
            <li><Link to="/what-is-a-timegrapher">What is a timegrapher? Readings explained</Link></li>
            <li><Link to="/measure-watch-accuracy">How to measure watch accuracy at home</Link></li>
            <li><Link to="/watch-running-fast">Why is my watch running fast or slow?</Link></li>
            <li><Link to="/brand-accuracy">Watch accuracy by brand: Rolex, Omega, Seiko</Link></li>
          </ul>
        </div>
      </article>
    </div>
  );
}

export default AccuracyGuidePage;
